import type {
  PaymentCollectionStatus,
  PaymentDepositRefundStatus,
  PaymentRefundReason,
  PaymentSettlementStatus,
  PaymentTab,
} from '@/types/payment.types';
import type { PayoutMethod } from '@/types/payout.types';

export const PAGE_SIZE = 20;
export const PAYMENTS_QUERY_KEY = 'payments';

export const PAYMENT_TABS: { key: PaymentTab; label: string }[] = [
  { key: 'awaiting', label: 'Awaiting' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'verified', label: 'Verified' },
  { key: 'refund_due', label: 'Refund due' },
  // Refund-due records live on here once actioned; without it they matched no tab and
  // dropped out of the panel entirely.
  { key: 'closed', label: 'Closed' },
  { key: 'to_settle', label: 'To settle' },
  { key: 'settled', label: 'Settled' },
  { key: 'disputed', label: 'Disputed' },
];

export const COLLECTION_STATUS_LABEL: Record<PaymentCollectionStatus, { label: string; tone: string }> = {
  awaiting_payment: { label: 'Awaiting payment', tone: 'bg-amber-100 text-amber-700' },
  payment_submitted: { label: 'Submitted', tone: 'bg-brand-50 text-brand-600' },
  verified: { label: 'Verified', tone: 'bg-emerald-100 text-emerald-700' },
  refund_due: { label: 'Refund due', tone: 'bg-red-100 text-red-700' },
  rejected: { label: 'Rejected', tone: 'bg-red-100 text-red-700' },
  refunded: { label: '✓ Refunded', tone: 'bg-emerald-100 text-emerald-700' },
};

/** Why the money is going back — the short line on the row and the drawer banner. */
export const REFUND_REASON_LABEL: Record<PaymentRefundReason, string> = {
  owner_no_response: 'Owner never responded',
  owner_declined: 'Owner declined after payment',
  cancelled: 'Cancelled after payment',
};

export const REFUND_DUE_BANNER =
  'This booking fell through with the money already collected. Transfer it back to the borrower, then record it here.';
export const REFUND_UNVERIFIED_WARNING =
  'This payment was never verified against the bank. Check the statement first — if the money never arrived, close it as never received instead of refunding.';
export const REFUND_MISSING_PAYOUT =
  'This borrower has no payout details and gave no UPI ID with their proof. Ask for them before recording the refund.';
export const REFUND_CONFIRM_LABEL = 'Confirm refund sent';
export const REFUND_NOT_RECEIVED_LABEL = 'No payment was received';

export const SETTLEMENT_STATUS_LABEL: Record<PaymentSettlementStatus, { label: string; tone: string }> = {
  pending: { label: 'Pending', tone: 'bg-[var(--color-surface-muted)] text-[var(--color-ink-muted)]' },
  paid: { label: 'Paid — awaiting owner', tone: 'bg-brand-50 text-brand-600' },
  owner_confirmed: { label: '✓ Owner confirmed', tone: 'bg-emerald-100 text-emerald-700' },
  disputed: { label: 'Disputed', tone: 'bg-red-100 text-red-700' },
};

export const DEPOSIT_STATUS_LABEL: Record<PaymentDepositRefundStatus, { label: string; tone: string }> = {
  pending: { label: 'Deposit pending', tone: 'bg-[var(--color-surface-muted)] text-[var(--color-ink-muted)]' },
  refunded: { label: 'Refunded — awaiting borrower', tone: 'bg-brand-50 text-brand-600' },
  borrower_confirmed: { label: '✓ Borrower confirmed', tone: 'bg-emerald-100 text-emerald-700' },
  forfeited: { label: 'Deposit forfeited', tone: 'bg-red-100 text-red-700' },
};

/** Label for a party's payout rail in the detail drawer. */
export const PAYOUT_METHOD_LABEL: Record<PayoutMethod, string> = {
  upi: 'UPI',
  bank: 'Bank transfer',
};

export const PAYOUT_MISSING_OWNER =
  'This owner has not added payout details, so there is nowhere to send the money. Remind them, then settle once they have.';
export const PAYOUT_MISSING_BORROWER =
  'This borrower has not added payout details and gave no UPI ID with their proof. Remind them before refunding the deposit.';
export const PAYOUT_REMIND_LABEL = 'Ask for payout details';
